import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, EmptyState } from '@dayon/design-system';

/**
 * THE CONSOLE HAD NO ERROR BOUNDARY AT ALL.
 *
 * An unhandled throw while rendering any screen unmounted the whole React tree
 * and left a blank page: no sidebar, no account menu, no sign-out, no sentence.
 * An operator mid-task — a withdrawal half-reviewed, a reason half-typed — got
 * a white screen and no indication whether what they had been doing had
 * happened.
 *
 * ── WHY A CLASS, AND WHY THERE IS NO HOOK FOR THIS ───────────────────────
 *
 * React catches render errors only through `componentDidCatch` /
 * `getDerivedStateFromError`, both of which are class-only. This is the one
 * place in this console that must be a class, and the comment is here so the
 * next person does not try to "modernise" it into a function and quietly
 * remove the boundary.
 *
 * ── WHAT IT DOES NOT CATCH, SAID PLAINLY ─────────────────────────────────
 *
 * Only errors thrown while RENDERING, in a lifecycle, or in a constructor
 * below it. NOT: a rejected promise from `api.get`, an error inside an event
 * handler, or anything thrown in an effect's async callback. Those are the
 * common failures in this console and they are already handled — every screen
 * catches its own and opens `useErrorDialog`, which is the only thing that can
 * say which operation did not happen.
 *
 * So this is the last resort, not the mechanism. If a reader finds an ordinary
 * API failure arriving here, a screen has stopped catching it.
 *
 * ── IT WRAPS THE ROUTES, NOT THE SHELL ───────────────────────────────────
 *
 * Mounted inside `<Shell>` rather than around it, deliberately. A screen that
 * throws should leave the sidebar, the brand scope and the account menu
 * standing, so the operator can navigate away or sign out. Wrapping the shell
 * too would take the navigation down with the screen and leave them with a
 * reload as the only option — which is the blank page again, politely.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* The console is the only place this is readable, and an operator will be
       asked for it. There is no error-reporting service wired to this app; if
       one is ever added, it goes here and nowhere else. */
    console.error('A console screen failed to render.', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <EmptyState
        icon="warning-circle"
        title="This screen could not be shown"
        action={
          <Button variant="primary" onClick={() => this.setState({ error: null })}>
            Try this screen again
          </Button>
        }
      >
        <p>
          Something went wrong while drawing this page. Nothing was saved or changed by it — if
          you were part-way through an action, it did not happen.
        </p>
        <p className="text-subtle">
          The rest of the console still works: use the menu on the left to go somewhere else. If
          this screen keeps failing, the browser&rsquo;s developer console holds the detail a
          developer needs.
        </p>
      </EmptyState>
    );
  }
}
